import { Loader2, MapPin, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  groupRiderDeliveries,
  isDeliveryEnRoute,
  type RiderDelivery,
  useMyRiderDeliveries,
} from "@/hooks/use-rider-deliveries";
import { useRiderEvents } from "@/hooks/use-rider-events";
import { formatPrice } from "@/lib/format-price";

type QueueGroup = {
  key: "waiting" | "ready" | "enRoute";
  labelKey: string;
  items: RiderDelivery[];
};

function orderItemSummary(items: RiderDelivery["order"]["items"]): string {
  if (!items || items.length === 0) {
    return "";
  }
  return items.map((item) => `${item.quantity}× ${item.name}`).join(" · ");
}

function OrderStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const labelKey = `rider.orderStatus.${status}`;
  const label = t(labelKey, status);
  const variant = status === "ready" ? "default" : "secondary";
  return <Badge variant={variant}>{label}</Badge>;
}

function DeliveryCard({ entry }: { entry: RiderDelivery }) {
  const { t } = useTranslation();
  const { delivery, order } = entry;
  const address = delivery.addressSnapshot;

  return (
    <Card className="transition hover:bg-accent/30">
      <CardContent className="space-y-2 p-4">
        <div className="flex items-center justify-between">
          <span className="text-base font-semibold">#{order.dailyNumber}</span>
          <div className="flex items-center gap-1">
            <OrderStatusBadge status={order.status} />
            {isDeliveryEnRoute(entry) ? (
              <Badge variant="outline">{t(`rider.queue.groupEnRoute`, "En camino")}</Badge>
            ) : null}
          </div>
        </div>
        <div className="text-sm text-foreground">
          {orderItemSummary(order.items) || t("rider.delivery.noItems", "Sin productos")}
        </div>
        <div className="flex items-start gap-2 text-sm text-muted-foreground">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="truncate">
            {address?.label ? `${address.label} — ` : ""}
            {address?.line1 ?? ""}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          {order.contactPhone ? (
            <span className="flex items-center gap-1 text-muted-foreground">
              <Phone className="h-3.5 w-3.5" />
              {order.contactName ? `${order.contactName} · ` : ""}
              {order.contactPhone}
            </span>
          ) : order.contactName ? (
            <span className="text-muted-foreground">{order.contactName}</span>
          ) : (
            <span />
          )}
          <span className="font-medium">{formatPrice(order.total, order.currency)}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function QueueSection({ group }: { group: QueueGroup }) {
  const { t } = useTranslation();
  if (group.items.length === 0) {
    return null;
  }
  return (
    <section className="space-y-2">
      <h2 className="px-1 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {t(group.labelKey, group.key)}
      </h2>
      <ul className="space-y-2">
        {group.items.map((entry) => (
          <li key={entry.delivery.id}>
            <Link to={`/rider/deliveries/${entry.delivery.id}`} className="block">
              <DeliveryCard entry={entry} />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function RiderQueuePage() {
  const { t } = useTranslation();
  const query = useMyRiderDeliveries();
  useRiderEvents();

  if (query.isPending) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const items = query.data ?? [];

  if (items.length === 0) {
    return (
      <div className="p-4">
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            {t("rider.queue.empty", "No tenés pedidos asignados.")}
          </CardContent>
        </Card>
      </div>
    );
  }

  const groups = groupRiderDeliveries(items);

  return (
    <div className="space-y-4 p-3">
      <QueueSection
        group={{
          key: "waiting",
          labelKey: "rider.queue.groupWaiting",
          items: groups.waiting,
        }}
      />
      <QueueSection
        group={{
          key: "ready",
          labelKey: "rider.queue.groupReady",
          items: groups.ready,
        }}
      />
      <QueueSection
        group={{
          key: "enRoute",
          labelKey: "rider.queue.groupEnRoute",
          items: groups.enRoute,
        }}
      />
    </div>
  );
}
